<template>
  <div class="modal fade" style="background-color: rgba(7,7,7,0.36);" id="go-to-location-dialog" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body p-4">
          <h3 class="mb-3"><strong>Search Location or GPS Coordinates</strong></h3>

          <!-- Tabs -->
          <ul class="nav bg-gray mb-3 position-relative rounded-pill p-1" id="goto-tabs" role="tablist" ref="nav">
            <div class="nav-indicator" ref="indicator"></div>
            <li role="presentation">
              <button
                class="nav-link rounded-pill active"
                id="goto-tab-location"
                data-bs-toggle="pill"
                data-bs-target="#goto-location-pane"
                type="button"
                role="tab"
                aria-controls="goto-location-pane"
                aria-selected="true"
              >
                Location
              </button>
            </li>
            <li role="presentation">
              <button
                class="nav-link rounded-pill"
                id="goto-tab-coords"
                data-bs-toggle="pill"
                data-bs-target="#goto-coords-pane"
                type="button"
                role="tab"
                aria-controls="goto-coords-pane"
                aria-selected="false"
              >
                Coords
              </button>
            </li>
          </ul>

          <!-- Tab content -->
          <div class="tab-content" id="goto-tab-content">
            <!-- Location Tab -->
            <div
              class="tab-pane fade show active"
              id="goto-location-pane"
              role="tabpanel"
              aria-labelledby="goto-tab-location"
            >
              <div class="input-group rounded-pill mb-3">
                <input
                  ref="locationInput"
                  autocomplete="off"
                  class="form-control p-3 rounded-pill"
                  id="location-input"
                  type="text"
                  v-model="locationSearch"
                  placeholder="Search for a location (e.g. London)"
                />
              </div>
              <div ref="locationDropdown" id="location-dropdown"></div>
            </div>

            <!-- Coords Tab -->
            <div
              class="tab-pane fade"
              id="goto-coords-pane"
              role="tabpanel"
              aria-labelledby="goto-tab-coords"
            >
              <!-- Format Selector -->
              <div class="mb-3">
                <select
                  v-model="selectedFormat"
                  class="form-select"
                  @change="updatePlaceholder"
                >
                  <option value="latlon">Lat/Long</option>
                  <option value="ukgrid">UK Grid Reference</option>
                  <option value="irishgrid">Irish Grid Reference</option>
                </select>
              </div>

              <div class="input-group rounded-pill mb-3">
                <input
                  ref="coordInput"
                  autocomplete="off"
                  class="form-control p-3 rounded-pill"
                  id="coord-input"
                  type="text"
                  v-model="coord"
                  :placeholder="placeholder"
                />
              </div>

              <div class="text-end mt-4 d-flex justify-content-center">
                <button class="btn btn-secondary me-2" data-bs-dismiss="modal">CANCEL</button>
                <button class="btn btn-primary" @click="goToLocation">GO TO LOCATION</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useStore } from 'vuex'; // Import useStore for Vuex access
import mapboxgl, { LngLat } from 'mapbox-gl';
// Assuming these utils are compatible with Vue 3 environment
// eslint-disable-next-line import/no-duplicates
// eslint-disable-next-line import/extensions,import/no-unresolved
import { GridRef, GridRefUtils } from '../../utils/helpers/GridRefPreference';
// eslint-disable-next-line import/extensions,import/no-unresolved
import { addRecentSearchToLocalStorage } from '../../utils/helpers/LocalStorageHelper';
import { Modal, Tab } from 'bootstrap';
import { stadiaGeocodingService } from '../../utils/services/StadiaGeocodingService';

// --- State ---
const locationSearch = ref(''); // For location search tab
const coord = ref(''); // For coords tab
const placeholder = ref('Enter Lat/Long (e.g. 53.123, -7.123)');
const selectedFormat = ref('latlon'); // Default to lat/lon
const gridRef = ref(null); // Will hold the grid reference utils instance
const isInitialized = ref(false);

// --- Template Refs ---
const locationInput = ref(null); // Ref for the location search input
const coordInput = ref(null); // Ref for the coords input
const locationDropdown = ref(null); // Ref for the dropdown container
const nav = ref(null); // Ref for the nav tabs container
const indicator = ref(null); // Ref for the tab indicator

// --- Store ---
const store = useStore(); // Get the store instance

// --- Tab Indicator Functions ---
const moveIndicator = (el) => {
  if (!nav.value || !indicator.value || !el) return;
  const navRect = nav.value.getBoundingClientRect();
  const btnRect = el.getBoundingClientRect();
  indicator.value.style.width = `${btnRect.width}px`;
  indicator.value.style.height = `${btnRect.height}px`;
  indicator.value.style.left = `${btnRect.left - navRect.left}px`;
  indicator.value.style.top = `${btnRect.top - navRect.top}px`;
};

const setActiveIndicator = () => {
  if (!nav.value) return;
  const activeTab = nav.value.querySelector('.nav-link.active');
  if (activeTab) moveIndicator(activeTab);
};

const handleResize = () => {
  setActiveIndicator();
};

// --- Lifecycle Hooks ---
onMounted(async () => {
  try {
    const gridRefUtils = await GridRefUtils.preference();
    gridRef.value = gridRefUtils; // Assign to ref's value

    // Set selected format based on user preference
    switch (gridRefUtils.gridRef) {
      case GridRef.GbGrid:
        selectedFormat.value = 'ukgrid';
        break;
      case GridRef.IeGrid:
        selectedFormat.value = 'irishgrid';
        break;
      case GridRef.NzGrid:
      case GridRef.LatLon:
      default:
        selectedFormat.value = 'latlon';
        break;
    }

    // Update placeholder based on selected format
    updatePlaceholder();
  } catch (error) {
    console.error("Error fetching grid reference preference:", error);
    placeholder.value = 'Error loading preferences. Enter Coordinates';
  }

  // Set up tab indicator animation
  nextTick(() => {
    const modalEl = document.getElementById('go-to-location-dialog');
    if (modalEl) {
      modalEl.addEventListener('shown.bs.modal', () => {
        if (isInitialized.value) return;
        const firstBtn = document.getElementById('goto-tab-location');
        if (firstBtn) {
          Tab.getOrCreateInstance(firstBtn).show();
          moveIndicator(firstBtn);
          isInitialized.value = true;
        }
      });
    }

    if (nav.value) {
      nav.value.addEventListener('shown.bs.tab', (e) => {
        moveIndicator(e.target);
      });
    }

    setActiveIndicator();
  });

  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

// --- Watchers ---
watch(locationSearch, (newValue) => {
  // Use template refs instead of querySelector
  const inputEl = locationInput.value;
  const dropdownEl = locationDropdown.value;

  // Ensure refs are available
  if (!inputEl || !dropdownEl) {
    console.warn("Template refs not yet available in watch");
    return;
  }

  const value = inputEl.value.trim(); // Access input value via ref

  // Clear dropdown if input is short
  if (value.length < 2) {
    dropdownEl.innerHTML = ''; // Still using innerHTML for simplicity in porting
    return;
  }

  // Fetch autocomplete suggestions from Stadia
  stadiaGeocodingService.autocomplete(value)
    .then((data) => {
      if (!dropdownEl) return; // Check again in case component unmounted
      dropdownEl.innerHTML = ''; // Clear previous suggestions

      data.forEach((location) => {
        const option = document.createElement('div');
        option.textContent = location.display_name;

        option.addEventListener('click', () => {
          if (!inputEl || !dropdownEl) return; // Check refs again

          inputEl.value = location.display_name; // Update input field display
          locationSearch.value = location.display_name; // Update the v-model ref *explicitly* if needed
          dropdownEl.innerHTML = ''; // Clear dropdown

          try {
            // --- Map Interaction ---
            if (location.boundingbox) {
              const bbox = location.boundingbox.map(parseFloat);
              const bounds = new mapboxgl.LngLatBounds(
                [bbox[2], bbox[0]], // Southwest coordinates
                [bbox[3], bbox[1]], // Northeast coordinates
              );
              // Assuming window.map is still the way map instance is accessed
              if (window.map) {
                window.map.fitBounds(bounds, { padding: 20 });
              } else {
                console.error("window.map is not defined");
              }

            } else if (location.lat && location.lon) {
              if (window.map) {
                window.map.flyTo({
                  center: [parseFloat(location.lon), parseFloat(location.lat)],
                  zoom: 15,
                });
              } else {
                console.error("window.map is not defined");
              }
            }

            // --- Hide Modal ---
            const element = document.getElementById('go-to-location-dialog');
            const modal = Modal.getInstance(element);
            modal.hide();
          } catch (mapError) {
            console.error("Error during map interaction or hiding modal:", mapError);
          }

        });
        dropdownEl.appendChild(option); // Append the new option div
      });
    })
    .catch((error) => console.error("Error fetching location suggestions:", error)); // Log fetch errors
});


// --- Methods ---
function updatePlaceholder() {
  switch (selectedFormat.value) {
    case 'latlon':
      placeholder.value = 'Enter Lat/Long (e.g. 53.123, -7.123)';
      break;
    case 'ukgrid':
      placeholder.value = 'Enter UK Grid Ref (e.g. SU 73520 96536)';
      break;
    case 'irishgrid':
      placeholder.value = 'Enter Irish Grid Ref (e.g. H 123456 654321)';
      break;
    default:
      placeholder.value = 'Enter coordinates';
  }
}

async function getLngLatFromCoord() {
  // Convert the coordinate using the selected format
  return convertCoord();
}

// Helper to convert coordinate based on selected format
function convertCoord() {
  const currentCoord = coord.value.trim();

  switch (selectedFormat.value) {
    case 'latlon':
      // Lat/Long format
      const cleanedCoord = currentCoord.replace(/,/g, ' ').trim();
      const parts = cleanedCoord.split(/\s+/);
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lon = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
          return new LngLat(lon, lat);
        }
      }
      throw new Error('Invalid Lat/Long format. Expected: latitude longitude (e.g., 53.123 -7.123)');

    case 'ukgrid':
      // UK Grid Reference
      const gbGridUtils = new GridRefUtils(GridRef.GbGrid);
      return gbGridUtils.convertLetteredGBGridRefToLatLng(currentCoord);

    case 'irishgrid':
      // Irish Grid Reference
      const ieGridUtils = new GridRefUtils(GridRef.IeGrid);
      return ieGridUtils.convertLetteredIrishGridRefToLonLat(currentCoord);

    default:
      throw new Error('Unknown coordinate format selected');
  }
}

async function goToLocation() {
  const currentGridRef = gridRef.value;
  const currentCoord = coord.value.trim();

  if (!currentGridRef) {
    alert("Grid reference system preferences not loaded yet. Please wait or reload.");
    return;
  }

  if (!currentCoord) {
    alert("Please enter coordinates.");
    return;
  }

  try {
    // Try to convert the coordinate (auto-detects format)
    const latLng = await getLngLatFromCoord();

    // Hide Modal
    const element = document.getElementById('go-to-location-dialog');
    const modal = Modal.getInstance(element);
    if (modal) {
      modal.hide();
    }

    // Fly to location on map
    if (window.map) {
      window.map.flyTo({
        center: latLng,
        zoom: 15,
        essential: true,
      });
    } else {
      console.error("window.map is not defined");
      alert("Map is not available to navigate.");
    }

    // Original marker code commented out, kept for reference
    // const popup = createPopup(latLng.lat, latLng.lng);
    // const el = store.getters['map/getEl']; // Assuming getter still exists
    // store.state.map.marker = new Marker(el) // Accessing state directly is discouraged, prefer mutations/actions
    //   .setLngLat([latLng.lng, latLng.lat])
    //   .setPopup(popup)
    //   .addTo(window.map);
    // popup.addTo(window.map);

  } catch(error) {
    console.error("Error in goToLocation:", error);
    alert(`${error.message || 'Invalid coordinate format. Please check your input and try again.'}`);
  }
}

</script>

<style>
/* Tabs container */
#goto-tabs {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  max-width: 380px;
  margin: auto;
}

#goto-tabs > li {
  flex: 1 1 0;
  display: flex;
}

#goto-tabs .nav-link {
  flex: 1 1 0;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
  font-weight: 600;
}

#goto-tabs .nav-link.active {
  color: var(--dark);
}

.nav-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  background-color: #fddf00;
  border-radius: 9999px;
  transition: left 0.3s ease, width 0.3s ease, top 0.3s ease, height 0.3s ease;
  z-index: 0;
}

/* Input styles */
#location-input,
#coord-input {
  width: 100%;
  border: none;
  outline: none;
}

/* Location dropdown */
#location-dropdown {
  position: absolute;
  width: calc(100% - 2 * 1.5rem);
  left: 1.5rem;
  max-height: 200px;
  overflow-y: auto;
  background-color: black;
  color: white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  z-index: 1056;
  border-radius: 0 0 0.25rem 0.25rem;
}

#location-dropdown div {
  padding: 0.75em 1em;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

#location-dropdown div:last-child {
  border-bottom: none;
}

#location-dropdown div:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Dark popup styles remain the same */
.dark-popup .mapboxgl-popup-content {
  background-color: #202020;
  color: white;
  font-size: 13px;
  padding: 1em;
  border-radius: 8px;
  max-height: 350px;
  overflow-y: auto;
}

.dark-popup .mapboxgl-popup-tip {
  border-top-color: #202020;
}

.mapboxgl-popup-anchor-top .mapboxgl-popup-tip{
  border-bottom-color: #202020 !important;
}

.dark-popup .popup-content {
  max-width: 300px;
}
</style>
